import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  CreditCard, CheckCircle2, AlertCircle, ArrowUpRight, RefreshCw, ExternalLink,
  Loader2, ShieldCheck, Unplug,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stripe Connect Express card for the wallet page.
 * Four states:
 *  - not connected → "Connect with Stripe" CTA
 *  - onboarding incomplete → "Continue onboarding" CTA + outstanding requirements
 *  - connected but payouts disabled → "Awaiting Stripe approval" info
 *  - fully connected → balance, "Withdraw via Stripe" button, Express Dashboard link
 *
 * Props:
 *  walletBalance — number, used to gate the Withdraw button
 *  onWithdrawComplete — () => void, called to refresh the parent's wallet state
 */
export default function StripeConnectCard({ walletBalance = 0, onWithdrawComplete, language = 'lv' }) {
  const isLV = language === 'lv';
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/stripe-connect/status`);
      setState(data);
      if (data?.connected && data?.payouts_enabled) {
        try {
          const { data: ps } = await axios.get(`${API}/wallet/stripe-connect/payout-status`);
          setPayoutStatus(ps);
        } catch (e) { /* non-fatal */ }
      }
    } catch (e) {
      console.error('stripe-connect status', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // If user returns from Stripe onboarding (?stripe=connected) — refresh state.
  useEffect(() => {
    const flag = searchParams.get('stripe');
    if (flag === 'connected' || flag === 'refresh') {
      (async () => {
        try {
          await axios.post(`${API}/wallet/stripe-connect/refresh`);
          await fetchStatus();
          toast.success(isLV ? 'Stripe statuss atjaunots!' : 'Stripe status refreshed!');
        } catch (e) {
          console.error('stripe refresh', e);
        } finally {
          // Strip the query param so a refresh doesn't re-trigger.
          searchParams.delete('stripe');
          setSearchParams(searchParams, { replace: true });
        }
      })();
    }
  }, [searchParams, setSearchParams, fetchStatus, isLV]);

  const onboard = async () => {
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/wallet/stripe-connect/onboard`, {
        origin_url: window.location.origin,
      });
      // Stripe-hosted URL — full page redirect
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isLV ? 'Stripe Connect kļūda' : 'Stripe Connect error'));
      setWorking(false);
    }
  };

  const refresh = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/wallet/stripe-connect/refresh`);
      await fetchStatus();
      toast.success(isLV ? 'Atjaunots!' : 'Refreshed!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isLV ? 'Atjaunināšana neizdevās' : 'Refresh failed'));
    } finally {
      setWorking(false);
    }
  };

  const openLoginLink = async () => {
    try {
      const { data } = await axios.post(`${API}/wallet/stripe-connect/login-link`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isLV ? 'Stripe Dashboard kļūda' : 'Stripe Dashboard failed'));
    }
  };

  const disconnect = async () => {
    if (!window.confirm(isLV ? 'Tiešām atvienot Stripe Connect kontu? Konts paliks Stripe pusē, bet KoLV vairs neveiks izmaksas uz to.' : 'Disconnect Stripe Connect? The account stays on Stripe, but KoLV will no longer transfer to it.')) return;
    try {
      await axios.post(`${API}/wallet/stripe-connect/disconnect`);
      await fetchStatus();
      toast.success(isLV ? 'Atvienots!' : 'Disconnected!');
    } catch (e) {
      toast.error(isLV ? 'Atvienošana neizdevās' : 'Disconnect failed');
    }
  };

  const submitWithdraw = async (e) => {
    e?.preventDefault();
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 5) {
      toast.error(isLV ? 'Minimums €5.00' : 'Min €5.00');
      return;
    }
    if (amount > walletBalance) {
      toast.error(isLV ? 'Nepietiekams atlikums' : 'Insufficient balance');
      return;
    }
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/wallet/stripe-connect/withdraw`, { amount });
      toast.success(data.message || (isLV ? 'Izmaksa nodota Stripe!' : 'Sent to Stripe!'));
      setShowWithdraw(false);
      setWithdrawAmount('');
      if (onWithdrawComplete) await onWithdrawComplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isLV ? 'Izmaksa neizdevās' : 'Withdraw failed'));
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 flex items-center justify-center" data-testid="stripe-connect-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#635BFF]" />
      </div>
    );
  }

  // State A: Not connected
  if (!state?.connected) {
    return (
      <div
        className="bg-white border border-[#E5E4E0] rounded-2xl p-6"
        data-testid="stripe-connect-not-connected"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F5F4FF] flex items-center justify-center shrink-0">
            <CreditCard className="w-6 h-6 text-[#635BFF]" />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg font-semibold text-[#121212] mb-1">
              {isLV ? 'Pieslēdz Stripe — sāc paš-pārvaldīt izmaksas' : 'Connect Stripe for self-service payouts'}
            </h3>
            <p className="text-sm text-[#5C5C5C] mb-4">
              {isLV
                ? 'Savieno savu Stripe Express kontu (5 minūšu reģistrācija), un Tu varēsi izmaksāt naudu uz savu banku jebkurā brīdī — bez gaidīšanas uz admin apstiprinājumu.'
                : 'Connect your Stripe Express account (5-min signup) and withdraw to your bank anytime — no admin approval delay.'}
            </p>
            <ul className="text-xs text-[#5C5C5C] space-y-1 mb-4">
              <li>✓ {isLV ? '1-2 darba dienu naudas saņemšana caur SEPA' : '1-2 business days via SEPA'}</li>
              <li>✓ {isLV ? 'Stripe pārvalda KYC + banku savienojumus' : 'Stripe handles KYC + bank linking'}</li>
              <li>✓ {isLV ? 'Tev paliek pilna kontrole pār savu izmaksas grafiku' : 'You keep full control over your payout schedule'}</li>
            </ul>
            <div className="flex items-center gap-2 text-xs bg-[#F5F4F1] rounded-lg px-3 py-2 mb-4" data-testid="stripe-min-payout-info">
              <ArrowUpRight className="w-3.5 h-3.5 text-[#635BFF] shrink-0" />
              <span className="text-[#5C5C5C]">
                {isLV
                  ? `Minimālā izmaksas summa: €${(state?.min_withdrawal ?? 5).toFixed(2)}`
                  : `Minimum payout amount: €${(state?.min_withdrawal ?? 5).toFixed(2)}`}
              </span>
            </div>
            <Button
              onClick={onboard}
              disabled={working}
              className="bg-[#635BFF] hover:bg-[#0A2540] text-white"
              data-testid="stripe-connect-onboard-btn"
            >
              {working ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {isLV ? 'Atver Stripe...' : 'Opening Stripe...'}</span>
              ) : (
                <span className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> {isLV ? 'Pieslēgties Stripe' : 'Connect with Stripe'}</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // State B: Connected but onboarding incomplete
  const onboardingComplete = state.details_submitted && state.payouts_enabled;

  if (!onboardingComplete) {
    return (
      <div
        className="bg-amber-50 border border-amber-200 rounded-2xl p-6"
        data-testid="stripe-connect-onboarding-incomplete"
      >
        <div className="flex items-start gap-4 mb-3">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg font-semibold text-[#121212] mb-1">
              {isLV ? 'Pabeidz Stripe onboarding' : 'Complete Stripe onboarding'}
            </h3>
            <p className="text-sm text-[#5C5C5C]">
              {isLV
                ? 'Stripe gaida vēl dažus datus, lai apstiprinātu Tavu kontu izmaksām.'
                : 'Stripe needs a few more details to approve payouts.'}
            </p>
          </div>
        </div>

        {state.requirements_currently_due?.length > 0 && (
          <div className="mb-3 bg-white border border-amber-200 rounded-xl p-3 text-xs">
            <div className="font-semibold text-amber-800 mb-1">{isLV ? 'Trūkstošie dati:' : 'Outstanding requirements:'}</div>
            <ul className="text-[#5C5C5C] space-y-0.5">
              {state.requirements_currently_due.map(req => (
                <li key={req} className="font-mono">• {req}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onboard}
            disabled={working}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="stripe-connect-resume-onboarding-btn"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLV ? 'Turpināt onboarding' : 'Continue onboarding')}
          </Button>
          <Button
            onClick={refresh}
            disabled={working}
            variant="outline"
            data-testid="stripe-connect-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${working ? 'animate-spin' : ''}`} />
            {isLV ? 'Atjaunot statusu' : 'Refresh status'}
          </Button>
        </div>
      </div>
    );
  }

  // State C: Fully connected
  return (
    <div
      className="bg-white border border-[#E5E4E0] rounded-2xl p-6"
      data-testid="stripe-connect-active"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-outfit text-lg font-semibold text-[#121212]">
                {isLV ? 'Stripe Connect aktīvs' : 'Stripe Connect active'}
              </h3>
              <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
                {isLV ? 'Izmaksas iespējotas' : 'Payouts enabled'}
              </span>
            </div>
            <p className="text-xs text-[#5C5C5C] font-mono break-all">
              {state.account_id}
            </p>
          </div>
        </div>
        <button
          onClick={disconnect}
          className="text-[#5C5C5C] hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors"
          title={isLV ? 'Atvienot' : 'Disconnect'}
          data-testid="stripe-connect-disconnect-btn"
        >
          <Unplug className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#635BFF] hover:bg-[#0A2540] text-white disabled:bg-[#D5D4D0] disabled:text-[#5C5C5C] disabled:cursor-not-allowed"
              disabled={walletBalance < 5}
              title={walletBalance < 5
                ? (isLV ? `Minimālā izmaksa €5.00 — pieejams €${walletBalance.toFixed(2)}` : `Minimum payout €5.00 — available €${walletBalance.toFixed(2)}`)
                : undefined}
              data-testid="stripe-connect-withdraw-btn"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              {isLV ? 'Izmaksāt caur Stripe' : 'Withdraw via Stripe'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isLV ? 'Izmaksāt naudu' : 'Withdraw funds'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitWithdraw} className="space-y-4">
              <div>
                <Label htmlFor="sc-amount">{isLV ? 'Summa (€)' : 'Amount (€)'}</Label>
                <Input
                  id="sc-amount"
                  type="number"
                  step="0.01"
                  min="5"
                  max={walletBalance}
                  value={withdrawAmount}
                  onChange={e => setWithdrawAmount(e.target.value)}
                  placeholder="50.00"
                  data-testid="sc-withdraw-amount-input"
                />
                <p className="text-xs text-[#5C5C5C] mt-1">
                  {isLV ? `Pieejams: €${walletBalance.toFixed(2)} · Min: €5.00` : `Available: €${walletBalance.toFixed(2)} · Min: €5.00`}
                </p>
              </div>

              {/* Repeat-payout fee warning */}
              {payoutStatus?.first_free_used && (
                <div
                  className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800"
                  data-testid="repeat-fee-warning"
                >
                  <strong>{isLV ? 'Šī ir' : 'This is'} {(payoutStatus.monthly_payouts_count || 0) + 1}. {isLV ? 'izmaksa šomēnes' : 'payout this month'}.</strong>{' '}
                  {isLV
                    ? `Apkalpošanas maksa €${payoutStatus.fee_amount.toFixed(2)} tiks atskaitīta no maciņa papildus izmaksas summai. Pirmā izmaksa nākamajā mēnesī būs bez maksas.`
                    : `A €${payoutStatus.fee_amount.toFixed(2)} service fee will be deducted from your wallet in addition to the payout amount. The first payout next month will be free.`}
                  {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                    <div className="mt-2 pt-2 border-t border-amber-300 font-mono">
                      {isLV ? 'No maciņa tiks atskaitīts' : 'Will be deducted from wallet'}: <strong>€{(parseFloat(withdrawAmount) + payoutStatus.fee_amount).toFixed(2)}</strong>
                    </div>
                  )}
                </div>
              )}

              {payoutStatus && !payoutStatus.first_free_used && (
                <div
                  className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800"
                  data-testid="first-free-banner"
                >
                  <strong>{isLV ? '🎁 Tava pirmā izmaksa šomēnes — bez maksas!' : '🎁 Your first payout this month — free!'}</strong>{' '}
                  {isLV
                    ? `Katra nākamā izmaksa šajā mēnesī = €${payoutStatus.fee_amount.toFixed(2)} apkalpošanas maksa.`
                    : `Each subsequent payout this month = €${payoutStatus.fee_amount.toFixed(2)} service fee.`}
                </div>
              )}

              <div className="text-xs text-[#5C5C5C] bg-[#F5F4F1] rounded-lg p-3">
                {isLV
                  ? 'Nauda tiek pārskaitīta uz Tavu Stripe Express kontu uzreiz. Stripe to izmaksā uz Tavu banku 1-2 darba dienu laikā saskaņā ar Tavu izmaksas grafiku.'
                  : 'Funds move to your Stripe Express balance immediately. Stripe pays out to your bank in 1-2 business days per your payout schedule.'}
              </div>
              <Button
                type="submit"
                disabled={working || !withdrawAmount || parseFloat(withdrawAmount) < 5 || parseFloat(withdrawAmount) > walletBalance}
                className="w-full bg-[#635BFF] hover:bg-[#0A2540] text-white"
                data-testid="sc-confirm-withdraw-btn"
              >
                {working ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {isLV ? 'Notiek...' : 'Processing...'}</span>
                ) : (isLV ? 'Apstiprināt izmaksu' : 'Confirm withdrawal')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Button
          onClick={openLoginLink}
          variant="outline"
          data-testid="stripe-connect-dashboard-btn"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          {isLV ? 'Stripe Dashboard' : 'Stripe Dashboard'}
        </Button>
      </div>

      {/* Monthly payout status — compact info row */}
      {payoutStatus && (
        <div
          className="text-xs text-[#5C5C5C] mb-2 flex items-center justify-between"
          data-testid="monthly-payout-status"
        >
          <span>
            {isLV ? 'Šomēnes izmaksāts' : 'This month'}: <strong className="text-[#121212]">{payoutStatus.monthly_payouts_count || 0}</strong>
            {' '}({isLV ? 'nākamā' : 'next'}:{' '}
            {payoutStatus.first_free_used
              ? <strong className="text-amber-700">€{payoutStatus.fee_amount.toFixed(2)} {isLV ? 'maksa' : 'fee'}</strong>
              : <strong className="text-emerald-700">{isLV ? 'BEZ maksas' : 'FREE'}</strong>})
          </span>
          <a href="/maksas-un-komisijas" className="text-[#D97736] hover:underline">
            {isLV ? 'Cenrādis' : 'Pricing'}
          </a>
        </div>
      )}

      <button
        onClick={refresh}
        disabled={working}
        className="text-xs text-[#5C5C5C] hover:text-[#121212] flex items-center gap-1.5"
      >
        <RefreshCw className={`w-3 h-3 ${working ? 'animate-spin' : ''}`} />
        {isLV ? 'Atjaunot statusu' : 'Refresh status'}
      </button>
    </div>
  );
}
